import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Sofa, FolderTree, Users, Mail, Eye } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";

const COLORS = ["#C5A059", "#F4F0EA", "#8C7345", "#5B4D33", "#A88A52"];

export default function AdminDashboard() {
    const t = useT();
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState([]);
    const [topViewed, setTopViewed] = useState(null);
    const [viewPeriod, setViewPeriod] = useState("7d");

    useEffect(() => {
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/activity", { params: { limit: 12 } }).then((r) => setActivity(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setTopViewed(null);
        api.get("/stats/top-viewed", { params: { period: viewPeriod } })
            .then((r) => setTopViewed(r.data.items ?? []))
            .catch(() => setTopViewed([]));
    }, [viewPeriod]);

    return (
        <div className="p-8 md:p-12 space-y-10" data-testid="dashboard">
            <header>
                <p className="overline">{t("admin.overview")}</p>
                <h1 className="font-serif text-4xl md:text-5xl mt-3">
                    {t("admin.greeting")} <em className="serif-italic text-melilux-gold">{t("admin.curator")}</em>
                </h1>
            </header>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-kpis">
                <KPI icon={Sofa} label={t("admin.kpi.pieces")} value={stats?.total_products ?? "—"} testid="kpi-products" />
                <KPI icon={FolderTree} label={t("admin.kpi.collections")} value={stats?.total_categories ?? "—"} testid="kpi-categories" />
                <KPI icon={Users} label={t("admin.kpi.visitors")} value={stats?.unique_visitors ?? "—"} testid="kpi-visitors" />
                <KPI icon={Mail} label={t("admin.kpi.unread")} value={stats?.unread_inquiries ?? "—"} accent testid="kpi-unread" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card title={t("admin.chart.byCollection")} testid="chart-category">
                    {stats?.category_distribution?.length ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie
                                    data={stats.category_distribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={90}
                                    paddingAngle={3}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {stats.category_distribution.map((_, i) => (
                                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ background: "#121212", border: "1px solid #2a2a2a", color: "#F4F0EA" }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyChart label={t("admin.noData")} />
                    )}
                    <Legend items={stats?.category_distribution || []} />
                </Card>

                <Card
                    title={t("admin.chart.topViewed")}
                    testid="chart-top-viewed"
                    className="lg:col-span-2"
                    actions={
                        <div className="flex border border-white/15" data-testid="top-viewed-period">
                            {["7d", "30d", "all"].map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setViewPeriod(p)}
                                    data-testid={`top-viewed-${p}`}
                                    className={`px-3 py-1.5 text-[0.62rem] uppercase tracking-[0.22em] transition-colors ${
                                        viewPeriod === p
                                            ? "text-melilux-gold bg-white/[0.05]"
                                            : "text-melilux-cream/50 hover:text-melilux-cream"
                                    }`}
                                >
                                    {t(`admin.range.${p}`)}
                                </button>
                            ))}
                        </div>
                    }
                >
                    {topViewed === null ? (
                        <EmptyChart label="…" />
                    ) : topViewed.length === 0 ? (
                        <EmptyChart label={t("admin.noData")} />
                    ) : (
                        <ul className="divide-y divide-white/5" data-testid="top-viewed-list">
                            {topViewed.map((p, i) => {
                                const max = topViewed[0]?.views || 1;
                                return (
                                    <li key={p.id} className="py-3 flex items-center gap-4" data-testid={`top-viewed-item-${p.id}`}>
                                        <span className="font-serif text-lg text-melilux-cream/40 w-5 shrink-0 text-right">{i + 1}</span>
                                        <div className="w-10 h-10 bg-melilux-surface shrink-0 overflow-hidden">
                                            {p.image && (
                                                <img src={resolveImg(p.image, 320)} loading="lazy" alt="" className="w-full h-full object-cover" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <a
                                                href={`/collections/${p.category_slug}/${p.slug}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-melilux-cream/85 truncate block hover:text-melilux-gold transition-colors"
                                            >
                                                {p.name}
                                            </a>
                                            <div className="h-1 mt-2 bg-white/10">
                                                <div className="h-1 bg-melilux-gold" style={{ width: `${Math.max(4, (p.views / max) * 100)}%` }} />
                                            </div>
                                        </div>
                                        <span className="inline-flex items-center gap-1.5 text-sm text-melilux-cream/70 shrink-0">
                                            <Eye size={13} strokeWidth={1.5} className="text-melilux-cream/40" />
                                            {p.views}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card title={t("admin.chart.availability")} testid="chart-status">
                    {stats?.status_distribution?.length ? (
                        <div className="space-y-5 py-4">
                            {stats.status_distribution.map((s, i) => {
                                const total = stats.status_distribution.reduce((a, b) => a + b.value, 0) || 1;
                                const pct = Math.round((s.value / total) * 100);
                                return (
                                    <div key={s.name}>
                                        <div className="flex items-center justify-between text-[0.7rem] uppercase tracking-[0.25em] text-melilux-cream/65">
                                            <span>{s.name}</span>
                                            <span>{s.value} · {pct}%</span>
                                        </div>
                                        <div className="h-1 mt-2 bg-white/10">
                                            <div className="h-1" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <EmptyChart label={t("admin.noData")} />
                    )}
                </Card>

                <Card title={t("admin.activity")} testid="activity-log" className="lg:col-span-2">
                    {activity.length === 0 ? (
                        <p className="text-sm text-melilux-cream/45 py-10 text-center">{t("admin.noActivity")}</p>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {capInquiries(activity, 5).map((a) => (
                                <li key={a.id} className="py-4 flex items-center justify-between gap-6">
                                    <div className="flex items-center gap-4 min-w-0">
                                        <div className={`w-1.5 h-1.5 ${dotColor(a.type)}`} />
                                        <span className="text-sm text-melilux-cream/85 truncate">{a.message}</span>
                                    </div>
                                    <span className="text-[0.65rem] uppercase tracking-[0.22em] text-melilux-cream/45 shrink-0">
                    {formatDate(a.created_at, t)}
                  </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
}

function KPI({ icon: Icon, label, value, accent, testid }) {
    return (
        <div
            className={`border border-white/10 p-6 bg-melilux-bg ${accent ? "border-melilux-gold/40" : ""}`}
            data-testid={testid}
        >
            <div className="flex items-center justify-between">
                <Icon size={16} className={accent ? "text-melilux-gold" : "text-melilux-cream/55"} strokeWidth={1.5} />
                <span className="text-[0.62rem] uppercase tracking-[0.28em] text-melilux-cream/45">{label}</span>
            </div>
            <div className={`font-serif text-5xl mt-6 ${accent ? "text-melilux-gold" : "text-melilux-cream"}`}>
                {value}
            </div>
        </div>
    );
}

function Card({ title, children, className = "", testid, actions }) {
    return (
        <div className={`border border-white/10 bg-melilux-bg p-6 md:p-7 ${className}`} data-testid={testid}>
            <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
                <p className="overline">{title}</p>
                {actions}
            </div>
            {children}
        </div>
    );
}

function EmptyChart({ label }) {
    return (
        <div className="h-[260px] flex items-center justify-center text-[0.7rem] uppercase tracking-[0.25em] text-melilux-cream/40">
            {label}
        </div>
    );
}

function Legend({ items }) {
    if (!items?.length) return null;
    return (
        <div className="mt-4 space-y-2">
            {items.map((it, i) => (
                <div key={it.name} className="flex items-center justify-between text-[0.72rem]">
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="uppercase tracking-[0.22em] text-melilux-cream/70">{it.name}</span>
                    </div>
                    <span className="text-melilux-cream/55">{it.value}</span>
                </div>
            ))}
        </div>
    );
}

// The feed is dominated by inquiry events; show at most `max` of them so other
// activity (new pieces, collections) stays visible.
function capInquiries(activity, max) {
    let count = 0;
    return activity.filter((a) => a.type !== "inquiry_received" || ++count <= max);
}

function dotColor(type) {
    if (type === "inquiry_received") return "bg-melilux-gold";
    if (type === "product_created") return "bg-melilux-cream";
    if (type === "category_created") return "bg-[#8C7345]";
    return "bg-white/40";
}

function formatDate(iso, t) {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = Math.floor((now - d) / 60000);
        if (diff < 1) return t("admin.time.justNow");
        if (diff < 60) return `${diff}${t("admin.time.mAgo")}`;
        if (diff < 60 * 24) return `${Math.floor(diff / 60)}${t("admin.time.hAgo")}`;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
        return "";
    }
}